import React, { useMemo, useRef, useState } from "react";
import { CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  useMarkNotificationsSeenMutation,
} from "@/store/rest_api";
import type Notification from "@/model/interface/Notification";
import { useSeenOnScreen } from "@/hooks/useSeenOnScreen";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 20;

export default function NotificationsCenter() {
  const [page, setPage] = useState(0);
  const { data: notificationsPage, isLoading, isFetching } = useGetNotificationsQuery({ page, size: PAGE_SIZE });
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();
  const [markSeen] = useMarkNotificationsSeenMutation();
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);

  const notifications = useMemo(() => notificationsPage?.items ?? [], [notificationsPage]);
  const hasUnread = notifications.some((notification) => notification.status !== "READ");
  const unseenIds = useMemo(() => {
    return notifications
      .filter((notification) => notification.status === "UNREAD")
      .map((notification) => notification.id);
  }, [notifications]);
  useSeenOnScreen(listRef, unseenIds, (ids) => {
    void markSeen(ids);
  });

  const handleClick = (notification: Notification) => {
    if (notification.status !== "READ") {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-foreground">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Notifications</h1>
          <p className="text-muted-foreground text-sm">Everything that's happened across your workspace.</p>
        </div>
        {hasUnread && (
          <Button variant="outline" size="sm" onClick={() => markAllAsRead()}>
            <CheckCheck className="mr-1 h-4 w-4" />
            Mark all read
          </Button>
        )}
      </div>

      <div ref={listRef} className="rounded-xl border bg-card divide-y">
        {notifications.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">No notifications yet</div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.id}
              data-item-id={notification.id}
              className={`flex w-full items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/50 ${
                notification.status !== "READ" ? "bg-muted/30" : ""
              }`}
              onClick={() => handleClick(notification)}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={`text-sm ${notification.status !== "READ" ? "font-semibold" : ""}`}>
                    {notification.title}
                  </p>
                  {notification.status !== "READ" && (
                    <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                  )}
                </div>
                {notification.message && (
                  <p className="mt-0.5 text-sm text-muted-foreground">{notification.message}</p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                </p>
              </div>
            </button>
          ))
        )}
      </div>

      {notificationsPage && notificationsPage.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0 || isFetching}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page + 1} of {notificationsPage.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={notificationsPage.last || isFetching}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
